// @ts-check

/**
 * @file Provides the shared HTML renderer.
 */
import {memo, useEffect, useMemo, useState} from "react"
import {Link, useLocalSearchParams} from "expo-router"
import RenderHtml, {defaultSystemFonts} from "react-native-render-html"
import {StyleSheet} from "react-native"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {useDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"
import Text from "@kaspernj/api-maker/build/utils/text"
import {Pressable, View} from "react-native-propforge"
import {attachmentMetadataText, parseSizeBytes} from "../form/form-html-editor/attachment-metadata"
import buildRoutePath from "../../shared/build-route-path"
import {resolveEditorUploadedFileImageSources} from "../form/form-html-editor/upload-session"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {ReactElement} from "react" */
/** @import {CustomBlockRenderer, CustomRendererProps, TBlock} from "react-native-render-html" */
/** @import {TextProps} from "react-native" */

/** @type {TextProps} */
const defaultTextProps = {
  allowFontScaling: true,
  selectable: true
}

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} SharedRenderHtmlProps
 * @property {string | null | undefined} source HTML string to render.
 * @property {string} [testID] Stable test id.
 */

/**
 * Renders HTML with default text styling from the app theme.
 * @param {SharedRenderHtmlProps & Record<string, unknown>} props HTML rendering props.
 * @returns {ReactElement | null} Rendered HTML content.
 */
function SharedRenderHtml({source, ...restProps}) {
  const defaultStyle = useDefaultStyle()
  const breakpoint = useBreakpoint()
  const [resolvedSourceState, setResolvedSourceState] = useState({
    source: /** @type {string | null | undefined} */ (undefined),
    value: /** @type {string | null} */ (null)
  })
  const resolvedSource = resolvedSourceState.source === source ? resolvedSourceState.value : null

  useEffect(() => {
    if (!source) return undefined

    let cancelled = false
    const resolveSource = async () => {
      try {
        const nextSource = await resolveEditorUploadedFileImageSources(source)

        if (cancelled) return

        setResolvedSourceState({source, value: nextSource})
      } catch (error) {
        if (cancelled) return

        reportHtmlSourceResolveFailure(error)
        setResolvedSourceState({source, value: source})
      }
    }

    void resolveSource()

    return () => {
      cancelled = true
    }
  }, [source])

  const widthStyle = /** @type {{width?: number}} */ (breakpoint.styling({
    base: {width: 320},
    smUp: {width: 540},
    mdUp: {width: 720},
    lgUp: {width: 960},
    xlUp: {width: 1140}
  }))
  const contentWidth = widthStyle.width ?? 320
  const defaultTextBaseStyle = defaultStyle?.Text

  const defaultTextStyle = useMemo(() => {
    if (!defaultTextBaseStyle) return undefined

    const flattened = StyleSheet.flatten(defaultTextBaseStyle)
    /** @type {Record<string, unknown>} */
    const result = {}

    for (const key in flattened) {
      const value = /** @type {Record<string, unknown>} */ (flattened)[key]
      if (value === undefined) continue
      result[key] = value
    }

    return result
  }, [defaultTextBaseStyle])

  const sourceProps = useMemo(() => ({html: resolvedSource ?? ""}), [resolvedSource])
  const renderers = useMemo(() => ({
    div: AttachmentRenderer
  }), [])

  const tagsStyles = useMemo(() => ({
    body: {
      fontFamily: /** @type {string | undefined} */ (defaultTextStyle?.fontFamily),
      ...defaultTextStyle
    }
  }), [defaultTextStyle])

  if (!source || resolvedSource === null) return null

  return (
    <RenderHtml
      contentWidth={contentWidth}
      defaultTextProps={defaultTextProps}
      renderers={renderers}
      source={sourceProps}
      systemFonts={defaultSystemFonts}
      tagsStyles={tagsStyles}
      {...restProps}
    />
  )
}

/**
 * Logs a recoverable HTML source resolution failure.
 * @param {unknown} error Error raised while resolving HTML references.
 * @returns {void} Nothing.
 */
function reportHtmlSourceResolveFailure(error) {
  console.warn("Could not resolve HTML upload references", error)
}

/**
 * Renders persisted WYSIWYG attachment cards.
 * @type {CustomBlockRenderer}
 */
const AttachmentRenderer = function AttachmentRenderer(props) {
  const {TDefaultRenderer, tnode} = props
  const attributes = tnode.attributes || {}
  const uploadedFileId = attributes["data-uploaded-file-id"]
  const filename = attributes["data-file-name"]
  const colors = useThemeColors()
  const themeName = useThemeName()
  const params = useLocalSearchParams()

  if (attributes["data-editor-attachment"] !== "true" || !uploadedFileId || !filename) {
    return <TDefaultRenderer {...props} />
  }

  const metadata = attachmentMetadataText({
    contentType: attributes["data-content-type"],
    sizeBytes: parseSizeBytes(attributes["data-file-size"])
  })
  const href = buildRoutePath(params, `/uploaded-files/${uploadedFileId}`)

  return (
    <Link asChild href={href}>
      <Pressable
        style={styles[`attachmentCard-${themeName}`] ||= {
          backgroundColor: colors.surfaceMuted,
          borderColor: colors.borderStrong,
          borderRadius: 6,
          borderWidth: 1,
          marginBottom: 8,
          padding: 10
        }}
        testID={`sharedRenderHtml/attachment-${uploadedFileId}`}
      >
        <View
          style={styles.attachmentContent ||= {
            gap: 4
          }}
          testID={`sharedRenderHtml/attachment-${uploadedFileId}/content`}
        >
          <Text
            numberOfLines={1}
            style={styles[`attachmentFilename-${themeName}`] ||= {
              color: colors.text,
              fontSize: 14,
              fontWeight: 700
            }}
            testID={`sharedRenderHtml/attachment-${uploadedFileId}/filename`}
          >
            {filename}
          </Text>
          {metadata &&
            <Text
              style={styles[`attachmentMetadata-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 12
              }}
              testID={`sharedRenderHtml/attachment-${uploadedFileId}/metadata`}
            >
              {metadata}
            </Text>
          }
        </View>
      </Pressable>
    </Link>
  )
}

export default memo(SharedRenderHtml)
